import store from '@/store';
import {
  getCreativeFormOptions,
  getCreativeInit,
  REQIRED_FIELDS,
} from '@/configs/creativeConfigs';
import ValidationService from '@/services/ValidationService';
import {
  Creative,
  ECreativeFormFields,
  EStatus,
  ISerializeData,
  TCreativeMultipleFields,
  TDictionary,
} from '@/types';
import { ICreative } from '@/types/campaign/creatives';
import { uniqueId, cloneDeep } from 'lodash';
import { getCreativeStorePath } from '@/helpers/creativeHelpers';
import { PREFIX_ID } from '../configs/creativeConfigs';
import { getStatusInit } from '../configs/mainConfigs';

export default class CreativeService {
  static uploadNewCreatives(creatives: Array<ICreative>): void {
    try {
      for (const creative of creatives) {
        store.commit(
          getCreativeStorePath('addCreativeToEditCreatives'),
          creative
        );
      }
    } catch (err) {
      console.error('uploadNewCreatives: ', err);
    }
  }

  static addCreatives(creatives: Array<Creative>) {
    try {
      for (const creative of creatives) {
        const newCreative = {
          ...getCreativeInit(),
          ...creative,
          id: String(creative.id),
          campaignId: creative.campaign_id,
          sourceImg: {
            img: creative.img,
            width: creative.width,
            height: creative.height,
          },
          cta: creative?.cta || creative?.cpa_text || '',
          fileId: creative.file_id,
          coordinates: creative.coordinates ? JSON.parse(creative.coordinates) : [],
        };

        store.commit(
          getCreativeStorePath('addCreativeToAllCreatives'),
          newCreative
        );
      }
    } catch (err) {
      console.error('addCreatives: ', err);
    }
  }

  static editCreative(creative: ICreative): void {
    store.commit(getCreativeStorePath('addCreativeToEditCreatives'), creative);
  }

  static cloneCreative(creative: ICreative): void {
    const newCreative = cloneDeep(creative);
    newCreative.id = uniqueId(PREFIX_ID);
    newCreative.status = getStatusInit().active;

    store.commit(
      getCreativeStorePath('addCreativeToAllCreatives'),
      newCreative
    );
  }

  static deleteCreative(creativeId: string): void {
    store.commit(
      getCreativeStorePath('deleteCreativeFromAllCreatives'),
      creativeId
    );
  }

  static changeStatus(creative: ICreative): void {
    if (creative.status.value === EStatus.ACTIVE) {
      store.commit(getCreativeStorePath('changeCreativeStatus'), [
        creative.id,
        getStatusInit().draft,
      ]);
    } else {
      store.commit(getCreativeStorePath('changeCreativeStatus'), [
        creative.id,
        getStatusInit().active,
      ]);
    }
  }

  static applyNewCreatives(
    creatives: TDictionary<ICreative>,
    multipleFields: TDictionary<TCreativeMultipleFields>
  ): void {
    try {
      const tempFields = {} as TDictionary<TDictionary<Array<string>>>;

      for (const creativeId in multipleFields) {
        for (const field in multipleFields[creativeId]) {
          if (!tempFields[creativeId]) {
            tempFields[creativeId] = {};
          }
          tempFields[creativeId][field] = multipleFields[creativeId][field]
            .filter((f) => f.value)
            .map((f) => f.value);

          tempFields[creativeId][field].unshift(creatives[creativeId][field]);
        }
      }

      let newCreative = null as ICreative | null;

      for (const creativeId in tempFields) {
        for (const title of tempFields[creativeId][ECreativeFormFields.title]) {
          for (const description of tempFields[creativeId][
            ECreativeFormFields.description
          ]) {
            for (const cta of tempFields[creativeId][ECreativeFormFields.cta]) {
              newCreative = cloneDeep(creatives[creativeId]);

              if (newCreative) {
                newCreative.id = uniqueId('new-');
                newCreative.url = creatives[creativeId].url;
                newCreative.title = title;
                newCreative.description = description;
                newCreative.cta = cta;
                newCreative.isNew = false;
              }

              store.commit(
                getCreativeStorePath('addCreativeToAllCreatives'),
                newCreative
              );
            }
          }
        }
      }
    } catch (err) {
      console.error('applyNewCreatives', err);
    }
  }

  static updateCreatives(creatives: TDictionary<ICreative>): void {
    try {
      for (const creativeId in creatives) {
        store.commit(
          getCreativeStorePath('updateCreativeInAllCreatives'),
          creatives[creativeId]
        );
      }
    } catch (err) {
      console.error('updateCreatives: ', err);
    }
  }

  static validateCreatives(creatives: TDictionary<ICreative>): void {
    try {
      for (const creativeId in creatives) {
        for (const field in creatives[creativeId]) {
          if (field in ECreativeFormFields) {
            creatives[creativeId].errors[field] = [];

            if (REQIRED_FIELDS.includes(field)) {
              if (field === ECreativeFormFields.url) {
                ValidationService.url(
                  creatives[creativeId][field],
                  creatives[creativeId].errors[field]
                );
              }

              ValidationService.required(
                creatives[creativeId][field],
                creatives[creativeId].errors[field]
              );
            }

            ValidationService.maxStringLength(
              creatives[creativeId][field],
              getCreativeFormOptions().maxSymbols[field],
              creatives[creativeId].errors[field]
            );
          }
        }
      }
    } catch (err) {
      console.error('validateCreatives: ', err);
    }
  }

  static validateMultipleFields(
    fields: TDictionary<TCreativeMultipleFields>
  ): void {
    try {
      for (const creativeId in fields) {
        for (const field in fields[creativeId]) {
          for (const item of fields[creativeId][field]) {
            item.errors = [];

            ValidationService.maxStringLength(
              item.value,
              getCreativeFormOptions().maxSymbols[field],
              item.errors
            );
          }
        }
      }
    } catch (err) {
      console.error('validateMultipleFields: ', err);
    }
  }

  static clearEditCreatives() {
    store.commit(getCreativeStorePath('clearEditCreatives'));
  }

  static fillCreativesField(
    creatives: ISerializeData<ICreative>,
    fieldName: string
  ): void {
    try {
      if (creatives.allIds.length > 1) {
        const firstCreative = creatives.byId[creatives.allIds[0]];

        for (const creativeId in creatives.byId) {
          if (creativeId !== firstCreative.id) {
            if (!creatives.byId[creativeId][fieldName]) {
              creatives.byId[creativeId][fieldName] = firstCreative[fieldName];
            }
          }
        }
      }
    } catch (err) {
      console.error('fillCreativesFromFirstCreative: ', err);
    }
  }

  static fillMultipleFields(
    creatives: TDictionary<TCreativeMultipleFields>,
    fieldName: string
  ): void {
    try {
      const creativeIds = Object.keys(creatives);

      if (creativeIds.length > 1) {
        const firstCreative = creatives[creativeIds[0]];

        for (const creativeId in creatives) {
          if (creativeId !== creativeIds[0]) {
            if (!creatives[creativeId][fieldName].length) {
              creatives[creativeId][fieldName] = cloneDeep(
                firstCreative[fieldName]
              );
            } else {
              for (const [index, item] of firstCreative[fieldName].entries()) {
                if (item.value) {
                  if (!creatives[creativeId][fieldName][index].value) {
                    creatives[creativeId][fieldName][index].value = item.value;
                  }
                }
              }
            }
          }
        }
      }
    } catch (err) {
      console.error('fillMultipleFieldsFromFirstCreative: ', err);
    }
  }

  static clearCreativeStore() {
    store.commit(getCreativeStorePath('clearCreativeStore'));
  }
}
